import { Button, ButtonDropdown, ClearableInput, Dialog, Select, Spinner } from '@rio-cloud/rio-uikit';
import { useIntl } from 'react-intl';
import { RestrictArea } from '../restrictArea';
import { useSupportGroups } from '../../hooks/useSupportGroups';
import { SUPPORT_GROUP_LEVELS, SupportGroupLevel } from '../../models/supportGroup';
import SupportGroupForm from './SupportGroupForm';

const SupportGroupsList = () => {
  const intl = useIntl();
  const {
    groups, loading, error, userHasAccess, searchTerm, setSearchTerm, level, setLevel, active, setActive,
    editingGroup, showForm, openNewForm, openEditForm, closeForm, saveGroup,
    groupToDelete, setGroupToDelete, confirmDelete, deleteLoading,
  } = useSupportGroups();

  if (userHasAccess === null && loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
      </div>
    );
  }

  if (userHasAccess === false) {
    return <RestrictArea showBackButton={false} />;
  }

  const levelOptions = SUPPORT_GROUP_LEVELS.map(item => ({ id: item, label: item }));
  const activeOptions = [
    { id: 'true', label: intl.formatMessage({ id: 'intl-msg:backoffice.supportGroups.active' }) },
    { id: 'false', label: intl.formatMessage({ id: 'intl-msg:backoffice.supportGroups.inactive' }) },
  ];

  return (
    <div className="p-6 max-w-[1000px] mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div className="text-size-h2">{intl.formatMessage({ id: 'intl-msg:backoffice.supportGroups.title' })}</div>
        <Button bsStyle={Button.PRIMARY} onClick={openNewForm} iconName="rioglyph-plus-sign">
          {intl.formatMessage({ id: 'intl-msg:backoffice.supportGroups.newGroup' })}
        </Button>
      </div>

      <div className="display-flex gap-10 margin-bottom-20">
        <div className="flex-1">
          <ClearableInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={intl.formatMessage({ id: 'intl-msg:backoffice.supportGroups.searchPlaceholder' })}
          />
        </div>
        <div className="width-150">
          <Select
            value={level ? [level] : []}
            options={levelOptions}
            onChange={option => setLevel(option?.id as SupportGroupLevel | undefined)}
            placeholder={intl.formatMessage({ id: 'intl-msg:backoffice.supportGroups.level' })}
            useClear
          />
        </div>
        <div className="width-150">
          <Select
            value={active === undefined ? [] : [String(active)]}
            options={activeOptions}
            onChange={option => setActive(option?.id ? option.id === 'true' : undefined)}
            placeholder={intl.formatMessage({ id: 'intl-msg:backoffice.supportGroups.status' })}
            useClear
          />
        </div>
      </div>

      {error && <p className="text-color-danger margin-bottom-15">{error}</p>}

      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
          <thead className="bg-gray-50 dark:bg-neutral-900">
            <tr>
              {['name', 'level', 'users', 'status'].map(column => (
                <th key={column} className="px-6 py-3 text-left font-medium text-gray-500 dark:text-neutral-400">
                  {intl.formatMessage({ id: `intl-msg:backoffice.supportGroups.${column}` })}
                </th>
              ))}
              <th className="px-6 py-3 w-[100px]" />
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-neutral-800 divide-y divide-gray-200 dark:divide-neutral-700">
            {groups.map(group => (
              <tr key={group.id} className="hover:bg-gray-50 dark:hover:bg-neutral-700">
                <td className="px-6 py-4 font-medium text-gray-900 dark:text-neutral-100">{group.name}</td>
                <td className="px-6 py-4">{group.level}</td>
                <td className="px-6 py-4">{group.users?.length ?? 0}</td>
                <td className="px-6 py-4">
                  {intl.formatMessage({ id: `intl-msg:backoffice.supportGroups.${group.active ? 'active' : 'inactive'}` })}
                </td>
                <td className="px-6 py-4 text-right">
                  <ButtonDropdown
                    title={<span className="rioglyph rioglyph-option-vertical" />}
                    items={[
                      { value: intl.formatMessage({ id: 'intl-msg:backoffice.supportGroups.edit' }), onSelect: () => openEditForm(group) },
                      { value: intl.formatMessage({ id: 'intl-msg:backoffice.supportGroups.delete' }), onSelect: () => setGroupToDelete(group) },
                    ]}
                    bsStyle="muted"
                    pullRight
                    noCaret
                    usePortal
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {groups.length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-neutral-400">
            {intl.formatMessage({ id: 'intl-msg:backoffice.supportGroups.empty' })}
          </div>
        )}
      </div>

      {showForm && (
        <Dialog
          show={showForm}
          onClose={closeForm}
          showCloseButton
          title={intl.formatMessage({ id: `intl-msg:backoffice.supportGroups.${editingGroup ? 'editGroup' : 'newGroup'}` })}
          bsSize={Dialog.SIZE_MD}
          body={<SupportGroupForm group={editingGroup} onSave={saveGroup} onCancel={closeForm} />}
        />
      )}

      <Dialog
        show={!!groupToDelete}
        onClose={() => setGroupToDelete(null)}
        title={intl.formatMessage({ id: 'intl-msg:backoffice.supportGroups.confirmDeleteTitle' })}
        bsSize={Dialog.SIZE_SM}
        body={intl.formatMessage({ id: 'intl-msg:backoffice.supportGroups.confirmDeleteMessage' }, { name: groupToDelete?.name })}
        footer={(
          <div className="display-flex justify-content-end gap-10">
            <Button bsStyle={Button.DEFAULT} onClick={() => setGroupToDelete(null)}>
              {intl.formatMessage({ id: 'intl-msg:backoffice.supportGroupForm.cancel' })}
            </Button>
            <Button bsStyle={Button.DANGER} onClick={confirmDelete} disabled={deleteLoading}>
              {intl.formatMessage({ id: 'intl-msg:backoffice.supportGroups.delete' })}
            </Button>
          </div>
        )}
      />
    </div>
  );
};

export default SupportGroupsList;
